import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import * as React from "react";

import { describeRequirement } from "@/shared/domain/layouts";

import { MatchFailure, MatchFailureReason } from "../layouts/LayoutRuleMatcher";
import {
  useDataNotFoundErrors,
  useEphemeralWorkspaceState,
} from "../WorkspaceCtx/hooks";

import styles from "./DataNotFoundMessage.module.css";

interface DataNotFoundMessageProps {
  panelId: string;
}

/**
 * Warns that a panel is missing data, listing one sentence per layout
 * requirement that bound to nothing in the workspace.
 *
 * Renders nothing while the workspace is hydrating: a panel's failures are only
 * replaced in the pass that ends hydration, so anything in the list before then
 * describes the layout being replaced. Dismissal is component state, so the
 * warning returns when the panel remounts.
 */
export function DataNotFoundMessage(props: DataNotFoundMessageProps) {
  const errors = useDataNotFoundErrors(props.panelId);
  const { isHydrating } = useEphemeralWorkspaceState();
  const [dismissed, setDismissed] = React.useState(false);

  if (dismissed || isHydrating || !errors || errors.length === 0) {
    return null;
  }

  return (
    <Alert
      severity="warning"
      onClose={() => setDismissed(true)}
      className={styles.top}
    >
      <AlertTitle>Couldn&apos;t load the following data</AlertTitle>
      <ul
        style={{
          margin: 0,
          paddingLeft: "1.5em",
          listStyle: "disc",
        }}
      >
        {errors.map((error) => {
          const message = formatReason(error.reason);
          return (
            <li
              key={`${error.ruleId}-${message}`}
              style={{ marginBottom: "0.5em" }}
            >
              {message}
            </li>
          );
        })}
      </ul>
    </Alert>
  );
}

/**
 * Turns one match failure into the sentence the alert lists for it.
 *
 * There is no default case, so a new {@link MatchFailure} member leaves the
 * function with a path that returns nothing and fails the type check until it
 * gets a case here.
 */
function formatReason(reason: MatchFailureReason): string {
  switch (reason.type) {
    case MatchFailure.NO_PATH_MATCH:
      return `No data in this workspace matches the path "${describeRequirement(reason.requirement)}"`;
    case MatchFailure.NO_DATATYPE_MATCH:
      return `Found data in this workspace matching the path "${describeRequirement(reason.requirement)}", but none with data type "${reason.dataType}"`;
    case MatchFailure.NO_COMPLETE_MATCH:
      return `Found more data in this workspace matching the path "${describeRequirement(reason.requirement)}" than can be used in this panel`;
    case MatchFailure.NO_REQUIREMENTS_SPECIFIED:
      return "This panel has no data requirements specified";
  }
}
